/**
 * 预测列表页导航状态（sessionStorage）
 * - 底部 Tab 切走再回来：恢复彩种 / Feed / 滚动 / 分页
 * - 进入详情再返回：同上
 * - 浏览器整页刷新：不恢复（reload buster 一次性标记）
 */

export const PREDICT_LIST_NAV_KEY = 'predict_list_nav_state'
export const PREDICT_LIST_RESTORE_SCROLL_KEY = 'predict_list_restore_scroll'
export const PREDICT_LIST_TAB_RETURN_KEY = 'predict_list_tab_return'
export const PREDICT_LIST_RELOAD_BUSTER_KEY = 'predict_list_reload_buster'

const SNAPSHOT_FN_KEY = '__predictListSnapshotFn'
const MAIN_TAB_PATHS = ['/', '/live', '/tool', '/mine']

/** 读取外层 Index 滚动容器（列表实际滚动在此元素上） */
export function getIndexBodyEl() {
  if (typeof document === 'undefined') {
    return null
  }
  return document.querySelector('.index-page .index-body') || document.querySelector('.index-body')
}

/** 读取已缓存的列表导航状态 */
export function readPredictListNavCache() {
  try {
    const raw = sessionStorage.getItem(PREDICT_LIST_NAV_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

/** 写入列表导航状态（彩种、Feed、scrollTop、listPage） */
export function writePredictListNavCache(data) {
  try {
    sessionStorage.setItem(PREDICT_LIST_NAV_KEY, JSON.stringify(data))
  } catch (e) {
    // ignore quota errors
  }
}

/** 标记：下次进入列表时从「底部 Tab 返回」恢复 */
export function markPredictTabReturn() {
  try {
    sessionStorage.setItem(PREDICT_LIST_TAB_RETURN_KEY, '1')
  } catch (e) {
    // ignore
  }
}

/** 标记：下次进入列表时从「详情页返回」恢复 */
export function markPredictDetailReturn() {
  try {
    sessionStorage.setItem(PREDICT_LIST_RESTORE_SCROLL_KEY, '1')
  } catch (e) {
    // ignore
  }
}

/** 清除所有「需要恢复滚动」的意图标记 */
export function clearPredictScrollRestoreIntents() {
  try {
    sessionStorage.removeItem(PREDICT_LIST_RESTORE_SCROLL_KEY)
    sessionStorage.removeItem(PREDICT_LIST_TAB_RETURN_KEY)
  } catch (e) {
    // ignore
  }
}

/** 应用启动时调用：若本次文档是 F5 刷新，写入一次性 buster */
export function noteDocumentReloadIfAny() {
  try {
    const nav = performance.getEntriesByType('navigation')[0]
    if (nav && nav.type === 'reload') {
      sessionStorage.setItem(PREDICT_LIST_RELOAD_BUSTER_KEY, '1')
    }
  } catch (e) {
    // ignore
  }
}

/** 列表页 created 时调用：消费刷新 buster，为 true 表示应置顶且不恢复 */
export function consumeReloadBuster() {
  try {
    if (sessionStorage.getItem(PREDICT_LIST_RELOAD_BUSTER_KEY) === '1') {
      sessionStorage.removeItem(PREDICT_LIST_RELOAD_BUSTER_KEY)
      return true
    }
  } catch (e) {
    // ignore
  }
  return false
}

/** 是否应在进入列表时恢复滚动（详情返回或 Tab 返回，且非刷新后首次） */
export function shouldRestorePredictScrollOnEnter() {
  try {
    return sessionStorage.getItem(PREDICT_LIST_RESTORE_SCROLL_KEY) === '1'
      || sessionStorage.getItem(PREDICT_LIST_TAB_RETURN_KEY) === '1'
  } catch (e) {
    return false
  }
}

/** 路由离开列表时，是否前往底部 Tab 主页面 */
export function isLeavingToMainTab(to) {
  const path = to && to.path ? to.path : ''
  return MAIN_TAB_PATHS.includes(path)
}

/** 是否为预测列表根路由（非搜索/详情等子页） */
export function isPredictListRoute(path) {
  return path === '/predict'
}

/** 注册列表快照函数，供 Index.goTab 在 push 前抓取彩种/Feed/分页 */
export function registerPredictListSnapshot(fn) {
  if (typeof window === 'undefined') {
    return
  }
  window[SNAPSHOT_FN_KEY] = fn
}

/** 卸载列表页时取消快照注册 */
export function unregisterPredictListSnapshot() {
  if (typeof window === 'undefined') {
    return
  }
  delete window[SNAPSHOT_FN_KEY]
}

/** 执行已注册的快照函数 */
function takePredictListSnapshot() {
  if (typeof window === 'undefined') {
    return null
  }
  const fn = window[SNAPSHOT_FN_KEY]
  return typeof fn === 'function' ? fn() : null
}

/** 无快照时仅合并 scrollTop 到旧缓存 */
function mergePredictListNavCacheFallback(scrollTop) {
  writePredictListNavCache({
    ...(readPredictListNavCache() || {}),
    scrollTop: scrollTop > 0 ? scrollTop : 0
  })
}

/**
 * 底部 Tab 切走前调用（须在 router.push 之前）
 * 由 Index.goTab 触发，此时 Predict 仍挂载
 */
export function preparePredictListTabLeave() {
  markPredictTabReturn()
  const el = getIndexBodyEl()
  const scrollTop = el ? el.scrollTop : 0
  const snap = takePredictListSnapshot()
  if (snap) {
    writePredictListNavCache({
      ...snap,
      activeLotteryId: snap.activeLotteryId,
      activeFeedTab: snap.activeFeedTab,
      listPage: snap.listPage,
      scrollTop: scrollTop > 0 ? scrollTop : (Number(snap.scrollTop) || 0)
    })
    return
  }
  mergePredictListNavCacheFallback(scrollTop)
}
